import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification } from '@kainos/validation';
import { queryKeys } from '@/lib/api';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications.api';

/** How often the bell looks for new notifications while a tab is open. */
const POLL_INTERVAL_MS = 60_000;

/**
 * The tray's list.
 *
 * Notifications arrive from workers, not from anything the person just did, so
 * the bell polls: without it a blocker raised at 10:05 would stay invisible
 * until the next full page load.
 */
export function useNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: getNotifications,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 30_000,
  });
}

/** Unread count for the badge. Reuses the tray query, so opening it is warm. */
export function useUnreadCount(): number {
  const { data } = useNotifications();
  return data?.filter((notification) => !notification.readAt).length ?? 0;
}

/**
 * Marks one notification read.
 *
 * The cache is updated before the request resolves: the person clicked a
 * notification to go somewhere, and waiting on a round trip to dim a dot would
 * make the tray feel broken. A failure rolls the list back.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const key = queryKeys.notifications.list();

  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Notification[]>(key);

      queryClient.setQueryData<Notification[]>(key, (current) =>
        current?.map((notification) =>
          notification.id === notificationId && !notification.readAt
            ? { ...notification, readAt: new Date().toISOString() }
            : notification,
        ),
      );

      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** Marks everything in the tray read. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const key = queryKeys.notifications.list();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Notification[]>(key);
      const readAt = new Date().toISOString();

      queryClient.setQueryData<Notification[]>(key, (current) =>
        current?.map((notification) =>
          notification.readAt ? notification : { ...notification, readAt },
        ),
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
